import React, { useEffect, useState } from 'react';
import './../Styles/Admin.css';
import './../Styles/Employee.css';
import './../Styles/LeadDashboard.css';
import axios from 'axios';
import ProfileDialog from './ProfileDialog';
import AppHeader from './AppHeader';
import Organization from './Organization';
import History from './History';
import GoalAssessmentTable from './GoalAssessmentTable';
import EmployeeDirectoryTable from './EmployeeDirectoryTable';
import EmployeeDetailsDialog from './EmployeeDetailsDialog';

const apiUrl = process.env.REACT_APP_API_URL;

const LeadDashboard = () => {
    const VALID_LEAD_SECTIONS = ['assigned', 'reviewed', 'organization', 'submitted'];
    const [currentSection, setCurrentSection] = useState(() => {
        const savedSection = localStorage.getItem('lead_currentSection');
        return VALID_LEAD_SECTIONS.includes(savedSection) ? savedSection : 'reviewed';
    });

    useEffect(() => {
        if (VALID_LEAD_SECTIONS.includes(currentSection)) {
            localStorage.setItem('lead_currentSection', currentSection);
        }
    }, [currentSection]);

    // User & Team state
    const [managerInfo, setManagerInfo] = useState({ full_name: '', job_title: '', employee_number: '' });
    const [teamEmployees, setTeamEmployees] = useState([]);
    const [leadObjectId, setLeadObjectId] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [isProfileDialogOpen, setProfileDialogOpen] = useState(false);

    useEffect(() => {
        const leadEmpId = localStorage.getItem('employeeId');
        if (!leadEmpId) {
            console.error('Lead Employee ID not found in local storage.');
            return;
        }

        // Fetch lead user info
        axios.get(`${apiUrl}/api/manager-info/${leadEmpId}`)
            .then(response => {
                setManagerInfo(response.data);
            })
            .catch(err => console.error('Error fetching lead info:', err));

        // Fetch team members under this lead (for Employees / Reviewed Goals section)
        axios.get(`${apiUrl}/api/get-lead-id/${leadEmpId}`)
            .then(res => {
                const leadId = res.data.lead_id || leadEmpId;
                setLeadObjectId(leadId);
                return axios.get(`${apiUrl}/api/employees-by-lead/${leadId}`);
            })
            .catch(err => {
                console.error('Error fetching lead ID, falling back to employee ID:', err);
                return axios.get(`${apiUrl}/api/employees-by-lead/${leadEmpId}`);
            })
            .then(res => {
                if (res && res.data) {
                    setTeamEmployees(res.data || []);
                }
            })
            .catch(err => console.error('Error fetching team employees:', err));
    }, []);

    useEffect(() => {
        if (currentSection === 'reviewed') {
            const leadEmpId = localStorage.getItem('employeeId');
            if (leadEmpId) {
                const idToUse = leadObjectId || leadEmpId;
                axios.get(`${apiUrl}/api/employees-by-lead/${idToUse}`)
                    .then(res => {
                        setTeamEmployees(res.data || []);
                    })
                    .catch(err => console.error('Error refreshing team employees:', err));
            }
        }
    }, [currentSection, leadObjectId]);

    const handleOpenProfileDialog = () => setProfileDialogOpen(true);
    const handleCloseProfileDialog = () => setProfileDialogOpen(false);

    return (
        <div className="lead-dashboard-page">
            {/* Top Header */}
            <AppHeader
                title="PERFORMANCE MANAGEMENT"
                employeeInfo={managerInfo}
                onProfileClick={handleOpenProfileDialog}
                hideLogo={false}
            />

            {/* Layout Body containing Left Sidebar and Right Main Content */}
            <div className="lead-body-wrapper">
                <aside className="lead-sidebar">
                    <nav className="nav-menu">
                        <a
                            href="#"
                            className={`nav-item ${currentSection === 'reviewed' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); setCurrentSection('reviewed'); }}
                        >
                            Employees
                        </a>
                        <a
                            href="#"
                            className={`nav-item ${currentSection === 'assigned' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); setCurrentSection('assigned'); }}
                        >
                            Assigned Goals
                        </a>
                        <a
                            href="#"
                            className={`nav-item ${currentSection === 'submitted' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); setCurrentSection('submitted'); }}
                        >
                            Submitted
                        </a>
                        <a
                            href="#"
                            className={`nav-item ${currentSection === 'organization' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); setCurrentSection('organization'); }}
                        >
                            Organization
                        </a>
                    </nav>
                </aside>

                <main className="lead-main-content">
                    {currentSection !== 'organization' && currentSection !== 'submitted' && (
                        <div className="welcome-section">
                            <h1>Hi {managerInfo.full_name ? managerInfo.full_name.split(' ')[0] : 'Lead'}...</h1>
                        </div>
                    )}

                    {/* Submitted section */}
                    {currentSection === 'submitted' && <History embedded={true} />}

                    {/* Assigned Goals section */}
                    {currentSection === 'assigned' && (
                        <GoalAssessmentTable
                            employeeId={managerInfo.employee_number || localStorage.getItem('employeeId')}
                        />
                    )}

                    {/* Team Members List (Reviewed section) */}
                    {currentSection === 'reviewed' && (
                        <EmployeeDirectoryTable
                            title="List of Employees"
                            employees={teamEmployees}
                            onViewDetails={(emp) => setSelectedEmployee(emp)}
                            showGoalActions={false}
                            showProjectFilter={true}
                            showRoleFilter={false}
                            showSearch={false}
                            loggedInLeadId={leadObjectId || managerInfo.employee_number || localStorage.getItem('employeeId')}
                        />
                    )}

                    {/* Organization section */}
                    {currentSection === 'organization' && (
                        <div className="organization-section-wrapper">
                            {leadObjectId ? (
                                <Organization 
                                    managerName={managerInfo.full_name ? managerInfo.full_name.split(' ')[0] : 'Lead'} 
                                    usersEndpoint={`${apiUrl}/api/employees-by-lead/${leadObjectId}`}
                                />
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center' }}>Loading organization data...</div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            {/* Team details review dialog */}
            {selectedEmployee && (
                <EmployeeDetailsDialog
                    employee={selectedEmployee}
                    reviewerRole="lead"
                    onClose={() => setSelectedEmployee(null)}
                />
            )}

            {/* Profile Dialog */}
            {isProfileDialogOpen && (
                <ProfileDialog
                    employeeInfo={managerInfo}
                    onClose={handleCloseProfileDialog}
                    onUpdate={(updatedData) => setManagerInfo(prev => ({ ...prev, ...updatedData }))}
                />
            )}
        </div>
    );
};

export default LeadDashboard;
